import React, { useCallback, useMemo, useEffect, useState } from 'react';
import axios from 'axios';
import ENVIRONMENT from '../../../../environments';
import CustomerMainLayout from '../CustomerMainLayout';
import DeleteEntityModal from '../../DeleteEntityModal/DeleteEntityModal';

const getIframeUserContextPayload = () => {
  const keys = [
    'username',
    'company_id',
    'shared_entities',
    'all3d_jwt_token',
    'user_role',
    'is_admin',
    'is_msprovider',
    'managed_company_id',
    'super_admin_username',
    'is_ai_customer',
  ];

  return keys.reduce((acc, key) => {
    const value = localStorage.getItem(key);
    if (value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const MigratedMaterialLibrary = ({ location, match }) => {
  const iframeRef = React.useRef(null);
  const migratedPortalBaseUrl = (ENVIRONMENT.MIGRATED_PORTAL_URL || process.env.REACT_APP_MIGRATED_PORTAL_URL || 'http://localhost:5173').replace(/\/$/, '');
  const materialId = match?.params?.id;
  const iframeSrc = materialId
    ? `${migratedPortalBaseUrl}/material-library/${materialId}`
    : `${migratedPortalBaseUrl}/material-library${location?.search || ''}`;

  const targetOrigin = useMemo(() => {
    try {
      return new URL(migratedPortalBaseUrl).origin;
    } catch (error) {
      return '*';
    }
  }, [migratedPortalBaseUrl]);

  // Subscription state
  const [subscriptionUpgradeManagerModel, setSubscriptionUpgradeManagerModel] = useState(false);
  const [currentRequestType, setCurrentRequestType] = useState('material');
  const [cardStatus, setCardStatus] = useState(0);

  // Delete modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);

  const sendToIframe = useCallback((msg) => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (iframeWindow) {
      iframeWindow.postMessage(msg, targetOrigin);
    }
  }, [targetOrigin]);

  const loadUserRequestStatus = useCallback(() => {
    const payload = {
      username: localStorage.getItem('username'),
      request_type: currentRequestType,
      action: 'check',
      category: '',
    };
    axios.post(ENVIRONMENT.PAYMENT_GATEWAY, payload).then((res) => {
      setCardStatus(res.data['card_status']);
      sendToIframe({ type: 'ALL3D_REFRESH_USER_REQUEST_STATUS' });
    });
  }, [currentRequestType, sendToIframe]);

  const postUserContext = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;

    iframeWindow.postMessage(
      {
        type: 'ALL3D_USER_CONTEXT',
        payload: getIframeUserContextPayload(),
      },
      targetOrigin
    );
  }, [targetOrigin]);

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleteLoader(true);
    try {
      const payload = {
        action: 'delete_material',
        material_id: itemToDelete.id,
        username: localStorage.getItem('username'),
        company_id: localStorage.getItem('company_id'),
      };
      await axios.post(ENVIRONMENT.MATERIAL_GET_3D, payload);
      sendToIframe({ type: 'MATERIAL_ITEM_DELETED', payload: { id: itemToDelete.id } });
      setDeleteModalVisible(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting material:', error);
    } finally {
      setDeleteLoader(false);
    }
  };

  useEffect(() => {
    const onMessage = (event) => {
      if (targetOrigin !== '*' && event.origin !== targetOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const { type, payload } = event?.data || {};

      if (type === 'ALL3D_USER_CONTEXT_REQUEST') {
        postUserContext();
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [postUserContext, targetOrigin, loadUserRequestStatus]);

  return (
    <CustomerMainLayout selected="39" remove_global_padding={true}>
      <div style={{ width: '100%', height: '100%' }}>
        <iframe
          ref={iframeRef}
          title="AI Material Library"
          src={iframeSrc}
          onLoad={postUserContext}
          style={{
            width: '100%',
            height: '100%',
            border: '0',
            display: 'block',
          }}
        />
      </div>

      <DeleteEntityModal
        visible={deleteModalVisible}
        onCancel={() => { setDeleteModalVisible(false); setItemToDelete(null); }}
        heading="Delete Material"
        subText="Are you sure you want to delete this material? This action cannot be undone."
        delete_model={handleConfirmDelete}
        selected_id={itemToDelete?.id}
        deletingModelLoader={deleteLoader}
      />

    </CustomerMainLayout>
  );
};

export default MigratedMaterialLibrary;
